/**
 * ManualDrawDialog — "Input Skating Order" walk-through modal (Feb 2026).
 *
 * Walks the user through each drawable event (Round 1 only; R2
 * events are draw-on-the-day, so excluded). For each event we show
 * a header + a table of skaters, each with a "Position" dropdown.
 * Once every skater has a position:
 *   - Confirmation msgbox: Draw Complete / Reset Draw
 *   - On complete: tile appears at the bottom of the modal
 *   - On reset: the event's dropdowns are wiped
 *
 * Bottom tiles are clickable — resets that event back to "current".
 *
 * Close mid-flow warning: Complete Draw / Download Partial Draw /
 * Abandon to Restart (per owner spec).
 *
 * Once every event has been drawn, "Save Draw" POSTs to
 * /api/order-of-skate/{cid}/manual-draw.
 */
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Info, AlertTriangle, RotateCcw, Download, Check } from 'lucide-react';

export default function ManualDrawDialog({ open, onOpenChange, competitionId, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [tiles, setTiles] = useState([]);           // full list from server
  const [currentIdx, setCurrentIdx] = useState(0);   // pointer into tiles
  const [positions, setPositions] = useState({});    // {tileIdx: {entry_id: pos}}
  const [completed, setCompleted] = useState({});    // {tileIdx: true}
  const [resetTileIdx, setResetTileIdx] = useState(null);
  const [closeWarnOpen, setCloseWarnOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Load tiles when opened
  useEffect(() => {
    if (!open || !competitionId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/order-of-skate/${competitionId}/manual-draw-events`,
          { withCredentials: true }
        );
        setTiles(res.data.events || []);
        setPositions({});
        setCompleted({});
        setCurrentIdx(0);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load events');
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, competitionId, onOpenChange]);

  const currentTile = tiles[currentIdx];
  const currentSkaters = currentTile?.skaters || [];
  const currentPositions = positions[currentIdx] || {};

  const positionsUsed = useMemo(() => new Set(
    Object.values(currentPositions).filter(v => v)
  ), [currentPositions]);

  const remainingPositions = (skaterIdx) => {
    const total = currentSkaters.length;
    const opts = [];
    for (let i = 1; i <= total; i++) {
      const used = Object.entries(currentPositions).some(
        ([sIdx, v]) => v === i && String(sIdx) !== String(skaterIdx)
      );
      if (!used) opts.push(i);
    }
    return opts;
  };

  const isCurrentFullyDrawn = currentTile
    && currentSkaters.length > 0
    && positionsUsed.size === currentSkaters.length;

  // Single-competitor events: pre-populate position 1 automatically
  // (Feb 2026 user request — user just clicks "Save and Next").
  useEffect(() => {
    if (!open) return;
    if (!currentTile) return;
    if (completed[currentIdx]) return;
    if (currentSkaters.length !== 1) return;
    const existing = positions[currentIdx] || {};
    if (existing[0]) return;
    setPositions(prev => ({
      ...prev,
      [currentIdx]: { ...(prev[currentIdx] || {}), 0: 1 },
    }));
  }, [open, currentIdx, currentTile, currentSkaters.length, completed, positions]);

  const handlePositionChange = (skaterIdx, value) => {
    setPositions(prev => ({
      ...prev,
      [currentIdx]: {
        ...(prev[currentIdx] || {}),
        [skaterIdx]: value ? parseInt(value, 10) : null,
      },
    }));
  };

  const handleCompleteEvent = () => {
    setCompleted(prev => ({ ...prev, [currentIdx]: true }));
    // Advance to next un-completed tile
    let next = currentIdx + 1;
    while (next < tiles.length && completed[next]) next++;
    setCurrentIdx(Math.min(next, tiles.length));
  };

  const handleResetCurrentEvent = () => {
    setPositions(prev => ({ ...prev, [currentIdx]: {} }));
  };

  const handleTileClick = (tileIdx) => {
    if (!completed[tileIdx]) return;
    setResetTileIdx(tileIdx);
  };

  const handleResetSavedTile = () => {
    const tIdx = resetTileIdx;
    setCompleted(prev => {
      const nxt = { ...prev };
      delete nxt[tIdx];
      return nxt;
    });
    setPositions(prev => ({ ...prev, [tIdx]: {} }));
    setCurrentIdx(tIdx);
    setResetTileIdx(null);
  };

  const allCompleted = tiles.length > 0
    && tiles.every((_, i) => completed[i]);

  const buildPayload = () => {
    const draws = [];
    for (let ti = 0; ti < tiles.length; ti++) {
      if (!completed[ti]) continue;
      const tile = tiles[ti];
      const posMap = positions[ti] || {};
      tile.skaters.forEach((sk, si) => {
        const pos = posMap[si];
        if (!pos) return;
        draws.push({
          event_number: tile.event_number,
          sub_block: tile.sub_block,
          skater_display: sk.skater_display,
          draw_no: pos,
        });
      });
    }
    return { draws };
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/order-of-skate/${competitionId}/manual-draw`,
        buildPayload(),
        { withCredentials: true }
      );
      toast.success(`Draw saved (${res.data.total_records} skaters).`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save draw');
    } finally {
      setSaving(false);
    }
  };

  const hasAnyProgress = Object.keys(completed).length > 0
    || Object.values(positions).some(p => Object.values(p || {}).some(v => v));

  const requestClose = () => {
    if (hasAnyProgress && !allCompleted) {
      setCloseWarnOpen(true);
    } else {
      onOpenChange(false);
    }
  };

  const handleDownloadPartial = async () => {
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/order-of-skate/${competitionId}/partial-draw-worksheet`,
        buildPayload(),
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = res.headers['content-disposition']?.match(/filename="?([^"]+)"?/)?.[1]
        || 'Partial_Draw_Worksheet.xlsx';
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Partial draw worksheet downloaded.');
      setCloseWarnOpen(false);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download worksheet');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) requestClose(); }}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
          data-testid="manual-draw-dialog"
          onEscapeKeyDown={(e) => { e.preventDefault(); requestClose(); }}
        >
          <DialogHeader>
            <DialogTitle>Input Skating Order</DialogTitle>
            <DialogDescription>
              Walk through each event and set the skating order for each skater.
              Order of skate for the second programme in an event (Round 2) will be set at the competition after Round 1 has been skated.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="py-8 text-sm text-stone-600" data-testid="manual-draw-loading">
              Loading events…
            </div>
          )}

          {!loading && tiles.length === 0 && (
            <div className="py-8 text-sm text-stone-600">
              No drawable events found for this competition.
            </div>
          )}

          {!loading && tiles.length > 0 && (
            <div className="flex-1 overflow-y-auto space-y-6 pr-2">
              {/* CURRENT event table */}
              {currentTile && !allCompleted && (
                <section data-testid="manual-draw-current-event" className="border rounded-lg p-4 bg-stone-50">
                  <h4 className="text-base font-semibold text-stone-800 mb-3">
                    {currentTile.header}
                  </h4>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-stone-500 border-b">
                        <th className="text-left py-1 pr-2">Skater(s)</th>
                        <th className="text-left py-1 pr-2 w-20">Art No</th>
                        <th className="text-left py-1 pr-2 w-20">Club</th>
                        <th className="text-left py-1 w-40">Order of Skate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentSkaters.map((sk, si) => (
                        <tr key={si} className="border-b border-stone-100">
                          <td className="py-2 pr-2">{sk.skater_display}</td>
                          <td className="py-2 pr-2 text-stone-600">{sk.artistic_number}</td>
                          <td className="py-2 pr-2 text-stone-600">{sk.club_abbreviation}</td>
                          <td className="py-2">
                            <select
                              className="border rounded px-2 py-1 bg-white w-24"
                              value={currentPositions[si] || ''}
                              onChange={(e) => handlePositionChange(si, e.target.value)}
                              data-testid={`manual-draw-position-${si}`}
                            >
                              <option value="">—</option>
                              {remainingPositions(si).map(pos => (
                                <option key={pos} value={pos}>{pos}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button
                      variant="outline"
                      onClick={handleResetCurrentEvent}
                      disabled={Object.keys(currentPositions).filter(k => currentPositions[k]).length === 0}
                      data-testid="manual-draw-reset-current-btn"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset Draw This Event
                    </Button>
                    <Button
                      onClick={handleCompleteEvent}
                      disabled={!isCurrentFullyDrawn}
                      className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
                      data-testid="manual-draw-save-next-btn"
                    >
                      Save and Next
                    </Button>
                  </div>
                </section>
              )}

              {allCompleted && (
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900"
                     data-testid="manual-draw-all-complete">
                  All events drawn. Click <span className="font-medium">Save Draw</span> to persist.
                </div>
              )}

              {/* Bottom accumulating tiles */}
              {tiles.some((_, i) => completed[i]) && (
                <section>
                  <h5 className="text-sm font-semibold text-stone-800 mb-2">Completed events</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {tiles.map((tile, ti) => completed[ti] && (
                      <button
                        key={ti}
                        type="button"
                        onClick={() => handleTileClick(ti)}
                        className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-white text-left hover:bg-stone-50 transition"
                        data-testid={`manual-draw-completed-tile-${ti}`}
                      >
                        <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                        <span className="text-xs text-stone-800 truncate">{tile.header}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={requestClose} disabled={saving}
                    data-testid="manual-draw-close-btn">
              Close
            </Button>
            <Button
              onClick={handleSaveAll}
              disabled={!allCompleted || saving}
              className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
              data-testid="manual-draw-save-btn"
            >
              {saving ? 'Saving…' : 'Save Draw'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset-already-saved-tile msgbox */}
      <AlertDialog open={resetTileIdx !== null} onOpenChange={(v) => { if (!v) setResetTileIdx(null); }}>
        <AlertDialogContent data-testid="manual-draw-reset-tile-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-[#5C4033]" />
              Reset the draw for this event?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Click &quot;Reset Draw&quot; below to complete a new draw for this event.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="manual-draw-reset-tile-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetSavedTile}
              className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
              data-testid="manual-draw-reset-tile-confirm"
            >
              Reset Draw
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Close-mid-flow warning */}
      <AlertDialog open={closeWarnOpen} onOpenChange={setCloseWarnOpen}>
        <AlertDialogContent className="sm:max-w-2xl" data-testid="manual-draw-close-warn-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Are you sure you want to abandon the draw?
            </AlertDialogTitle>
            <AlertDialogDescription>
              If the draw is closed at this point progress made so far will be lost.
              Please select one of the three options below to continue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setCloseWarnOpen(false)}
                    data-testid="manual-draw-close-warn-complete">
              Complete Draw
            </Button>
            <Button
              variant="outline"
              onClick={handleDownloadPartial}
              disabled={downloading}
              data-testid="manual-draw-close-warn-download"
            >
              <Download className="h-4 w-4 mr-2" />
              {downloading ? 'Downloading…' : 'Download Partial Draw'}
            </Button>
            <Button
              onClick={() => {
                setCloseWarnOpen(false);
                setPositions({});
                setCompleted({});
                setCurrentIdx(0);
                onOpenChange(false);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="manual-draw-close-warn-abandon"
            >
              Abandon to Restart
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
